/**
 * DAS-2442: Alert Effectiveness coloring helpers.
 *
 * The Alert Effectiveness panel renders four kinds of rows:
 *   - Shareprice (AE_AVG_abs_*)            — raw stock return after the alert
 *   - vs Portfolio (AE_AVG_P_*)            — alert-vs-portfolio spread
 *   - vs DeGiro    (AE_DG_P_*)             — alert-vs-DeGiro spread
 *   - vs S&P 500   (AE_AVG_SP_*)           — alert-vs-S&P spread
 *
 * The three "vs *" rows are already sign-flipped per alert type by the backend
 * (DR_Alert_Effectiveness_update.py, DAS-2332). Positive = "following the
 * alert beat doing nothing", regardless of buy/sell direction.
 *
 * The Shareprice row stores the RAW stock return — no backend flip. A naive
 * "negative=red, positive=green" rule then gives misleading colour for sell
 * alerts: e.g. a Harvest alert that correctly identified a falling stock
 * shows a red Shareprice value (looks bad) on the same panel where the three
 * benchmark spreads are green (look good) — the "3 green + 1 red" pattern
 * surfaced in DAS-2438.
 *
 * This module provides:
 *   - sharepriceCellColor(value, alertType)    — direction-aware colour for
 *     the Shareprice / Abs rows.
 *   - spreadCellColor(value)                   — raw sign-based colour for the
 *     vs-X rows (kept simple — the backend already did the flip).
 *
 * Colour constants match the existing palette in alertClauseService.js so
 * Shareprice cells look identical to the vs-X cells when both convey the
 * same "good"/"bad" meaning.
 */

export const AE_COLOR_GREEN = '#3daa6e';
export const AE_COLOR_RED   = '#cf1322';
export const AE_COLOR_NEUTRAL = 'inherit';

/**
 * Direction-aware colour for the Shareprice (AE_AVG_abs_*) row.
 *
 *   - Harvest / Sell: a falling stock IS the success signal → negative → green.
 *   - Accumulate / Buy: a rising stock IS the success signal → positive → green.
 *   - Monitor / Step In / unknown: no clear directional intent → fall back to
 *     the raw sign rule (positive → green, negative → red).
 *
 * @param {number|string|null} value The Shareprice value. Accepts numeric or
 *   numeric-string; non-numeric or null returns the neutral colour.
 * @param {string|null} alertType Lowercased substring match on 'harvest',
 *   'sell', 'accumulate', 'buy'. Anything else → raw sign rule.
 * @returns {string} A CSS colour string.
 */
export function sharepriceCellColor(value, alertType) {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(num) || num === 0) return AE_COLOR_NEUTRAL;

  const at = String(alertType || '').toLowerCase();
  const isSellSignal = at.includes('harvest') || at.includes('sell');
  const isBuySignal = at.includes('accumulate') || at.includes('buy');

  if (isSellSignal) {
    return num < 0 ? AE_COLOR_GREEN : AE_COLOR_RED;
  }
  // Buy signals and the "no clear direction" alert types (Monitor / Step In)
  // share the same raw rule: positive = green, negative = red. For Buy that
  // means "stock rose = correct"; for Monitor / Step In it means "stock rose,
  // make of it what you will".
  return num > 0 ? AE_COLOR_GREEN : AE_COLOR_RED;
  // Note for Buy explicitly (documented intent): even though `isBuySignal`
  // would route the same way as the fallback above, we keep the structure
  // explicit in `isSellSignal` so a future alert type with non-trivial
  // semantics (e.g. "Hedge") can be added by extending this function alone.
  // The current fallback intentionally favours "raw sign" for unknown types
  // so behaviour stays predictable rather than silently wrong.
}

/**
 * Raw sign-based colour for the three "vs *" rows (vs Portfolio / vs DeGiro /
 * vs S&P 500). The backend (DAS-2332) has already sign-flipped these values
 * per alert type, so a simple positive=green / negative=red rule is correct
 * for every alert type.
 *
 * Kept as a separate exported helper (rather than inlined) so that future
 * changes to the "vs-X" colour convention have a single editing point and
 * are testable in isolation.
 */
export function spreadCellColor(value) {
  const num = typeof value === 'number' ? value : parseFloat(value);
  if (!Number.isFinite(num) || num === 0) return AE_COLOR_NEUTRAL;
  return num > 0 ? AE_COLOR_GREEN : AE_COLOR_RED;
}
